import React, { useState } from 'react'
import Menu from 'aldoo-ra/Menu'
import Button from 'aldoo-ra/Button'
import Typography from 'aldoo-ra/Typography'
import Tooltip from 'aldoo-ra/Tooltip'
import Spinner from 'aldoo-ra/Spinner'
import { showNotification } from 'aldoo-ra/Notification'
import {
  XMarkIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  RocketLaunchIcon,
  Cog6ToothIcon,
  PlusCircleIcon,
  TrashIcon,
  EyeIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  BookmarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

// Import plugins
import SEOPlugin from './plugins/seo'
import PublishPlugin from './plugins/publish'
import SettingsPlugin from './plugins/settings'

const Toolbar = ({ editor }) => {
  const [activePlugin, setActivePlugin] = useState(null)

  // Plugin instances
  const plugins = {
    seo: SEOPlugin,
    publish: PublishPlugin,
    settings: SettingsPlugin,
  }

  const handlePluginOpen = (pluginName) => {
    setActivePlugin(pluginName)
  }

  const handlePluginClose = () => {
    setActivePlugin(null)
  }

  const handleSave = async () => {
    await editor.save()
  }

  const handleClose = () => {
    editor.close()
  }

  const handleAddSlot = () => {
    if (editor.addSlot) {
      editor.addSlot()
    }
  }

  const handleDelete = () => {
    if (editor.deletePage) {
      editor.deletePage()
    }
  }

  const handlePreview = async () => {
    // Save the page first
    await editor.save()

    // Get the page URL slug from link or slug field
    // editor.content now uses a getter that always returns current pageRef.current.content
    let pageUrl = null

    // Check for slug field first (from Publish Settings)
    if (editor.content?.slug) {
      pageUrl = editor.content.slug
    } else {
      // Fall back to link array
      const pageLink = editor.content?.link
      if (pageLink && Array.isArray(pageLink) && pageLink.length > 0) {
        const linkContent = pageLink[0].content || pageLink[0]
        pageUrl = linkContent.url
      }
    }

    // Normalize URL to ensure it starts with /
    if (pageUrl && !pageUrl.startsWith('/')) {
      pageUrl = '/' + pageUrl
    }

    // Check if URL exists and is not empty
    if (!pageUrl || pageUrl === '/' || pageUrl.trim() === '') {
      showNotification({
        message: 'Please enter a URL slug in Publish Settings before previewing the page',
        className: 'bg-admin_warning text-white'
      })
      return
    }

    // Open in new tab
    window.open(pageUrl, '_blank')
  }

  // Toolbar menu structure
  const toolbarMenu = {
    label: 'Page Tools',
    items: [
      {
        label: 'SEO',
        options: { action: 'openSEO' },
        render: (item) => {
          const isActive = activePlugin === 'seo'
          return (
            <div className="flex flex-row items-center gap-2 h-[34px]">
              <Tooltip content="SEO Settings" variant="dark">
                <DocumentTextIcon className={`w-6 h-6 transition-colors ${isActive ? 'text-admin_primary' : 'text-admin_text hover:text-admin_primary'}`} />
              </Tooltip>
              <span className={`text-sm ${isActive ? 'text-admin_primary font-semibold' : 'text-admin_text'}`}>{item.label}</span>
            </div>
          )
        },
      },
      {
        label: 'Settings',
        options: { action: 'openSettings' },
        render: (item) => {
          const isActive = activePlugin === 'settings'
          return (
            <div className="flex flex-row items-center gap-2 h-[34px]">
              <Tooltip content="Page Settings" variant="dark">
                <Cog6ToothIcon className={`w-6 h-6 transition-colors ${isActive ? 'text-admin_primary' : 'text-admin_text hover:text-admin_primary'}`} />
              </Tooltip>
              <span className={`text-sm ${isActive ? 'text-admin_primary font-semibold' : 'text-admin_text'}`}>{item.label}</span>
            </div>
          )
        },
      },
      {
        label: 'Publish',
        options: { action: 'openPublish' },
        render: (item) => {
          const isActive = activePlugin === 'publish'
          // Handle status - it might be a string or an object with value property
          const rawStatus = editor.content?.status
          const status = typeof rawStatus === 'object' && rawStatus?.value
            ? rawStatus.value
            : (typeof rawStatus === 'string' ? rawStatus : 'draft')

          // Status color mapping
          const getStatusColor = (status) => {
            switch (status) {
              case 'published':
                return 'text-admin_success'
              case 'scheduled':
                return 'text-admin_warning'
              case 'draft':
              default:
                return 'text-admin_text_secondary'
            }
          }

          const statusColor = getStatusColor(status)
          const iconColor = isActive ? 'text-admin_primary' : statusColor

          return (
            <div className="flex flex-row items-center gap-2 h-[34px]">
              <Tooltip content="Publish Settings" variant="dark">
                <RocketLaunchIcon className={`w-6 h-6 transition-colors ${iconColor} hover:text-admin_primary`} />
              </Tooltip>
              <div className="flex flex-col items-start justify-center gap-0">
                <span className={`text-sm leading-tight ${isActive ? 'text-admin_primary font-semibold' : 'text-admin_text'}`}>{item.label}</span>
                <span className={`text-[10px] leading-none capitalize ${statusColor}`}>
                  {status}
                </span>
              </div>
            </div>
          )
        },
      },
      {
        label: 'Preview',
        options: { action: 'preview' },
        render: (item) => (
          <div className="flex flex-row items-center gap-2 h-[34px]">
            <Tooltip content="Preview Page" variant="dark">
              <EyeIcon className="w-6 h-6 text-admin_text hover:text-admin_primary transition-colors" />
            </Tooltip>
            <span className="text-sm text-admin_text">{item.label}</span>
          </div>
        ),
      },
    ],
  }

  const actionMenu = {
    label: 'Actions',
    items: [
      {
        label: 'Add Slot',
        options: { action: 'addSlot' },
        render: (item) => (
          <Button
            variant="admin_secondary"
            disabled={editor.readOnly}
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            {item.label}
          </Button>
        ),
      },
      {
        label: 'Save Page',
        options: { action: 'save' },
        render: (item) => (
          <Button
            variant="admin_primary"
            disabled={editor.readOnly || editor.isSaving}
            onClick={handleSave}
          >
            {editor.isSaving ? (
              <>
                <Spinner size="sm" variant="admin_primary" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                {item.label}
              </>
            )}
          </Button>
        ),
      },
      {
        label: 'Delete',
        options: { action: 'delete' },
        render: (item) => (
          <Tooltip content="Delete Page" variant="dark">
            <div className="flex items-center justify-center h-[40px]">
              <TrashIcon className="w-6 h-6 text-admin_text hover:text-admin_error transition-colors" />
            </div>
          </Tooltip>
        ),
      },
      {
        label: 'Close',
        options: { action: 'close' },
        render: (item) => (
          <Tooltip content="Close Editor" variant="dark">
            <div className="flex items-center justify-center h-[40px]">
              <XMarkIcon className="w-6 h-6 text-admin_text hover:text-admin_error transition-colors" />
            </div>
          </Tooltip>
        ),
      },
    ],
  }

  const actionMap = {
    openSEO: () => handlePluginOpen('seo'),
    openPublish: () => handlePluginOpen('publish'),
    openSettings: () => handlePluginOpen('settings'),
    addSlot: handleAddSlot,
    preview: handlePreview,
    save: handleSave,
    delete: handleDelete,
    close: handleClose,
  }

  return (
    <>
      {/* Toolbar Header */}
      <div className="bg-admin_paper border-b border-admin_bordered shadow-md">
        <div className="px-6 py-4 flex items-center justify-between">
          {/* Page Title */}
          <div className="flex items-center gap-4">
            <Typography variant="admin_h4" className="my-0">
              {editor.content?.name || editor.page?.name || 'Untitled Page'}
            </Typography>
            {editor.content?.id && (
              <Typography variant="admin_txt1_dim" className="text-sm font-mono">
                {editor.content.id}
              </Typography>
            )}
          </div>

          {/* Toolbar Menus */}
          <div className="flex items-center gap-6">
            <Menu
              actionMap={actionMap}
              menuData={toolbarMenu}
              renderAs="hlist"
              itemWidth="auto"
            />
            <div className="h-6 w-px bg-admin_bordered" />
            <Menu
              actionMap={actionMap}
              menuData={actionMenu}
              renderAs="hlist"
              itemWidth="auto"
            />
          </div>
        </div>
      </div>

      {/* Active Plugin Panel */}
      {activePlugin && plugins[activePlugin] && (
        <div className="bg-admin_canvas border-b border-admin_bordered">
          {React.createElement(plugins[activePlugin], {
            editor,
            onClose: handlePluginClose,
          })}
        </div>
      )}
    </>
  )
}

export default Toolbar
